import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {DatabaseCode, toDatabaseCode} from "./database-code";

export const VolumeInventorySnapshotEntryValidator = t.intersection([
  t.type({
    dttm: t.number,
    volume: t.number,
    unusableVolume: t.number,
    secondaryVolume: t.number,
    secondaryUnusableVolume: t.number,
    pipelineVolume: t.number,
    pipelineUnusableVolume: t.number,
    pipelineSecondaryVolume: t.number,
    pipelineSecondaryUnusableVolume: t.number,
  }),
  t.partial({}),
]);

export const VolumeInventorySnapshotEntryArrayValidator = t.array(VolumeInventorySnapshotEntryValidator);

export type VolumeInventorySnapshotEntrySnapshotEntry = t.TypeOf<typeof VolumeInventorySnapshotEntryValidator>;
export type VolumeInventorySnapshotEntryArray = t.TypeOf<typeof VolumeInventorySnapshotEntryArrayValidator>;

@Exclude()
export class VolumeInventorySnapshotEntryEntity {
  @Expose()
  dttm!: number

  @Expose()
  volume!: number

  @Expose()
  unusableVolume!: number

  @Expose()
  secondaryVolume!: number

  @Expose()
  secondaryUnusableVolume!: number

  @Expose()
  pipelineVolume!: number

  @Expose()
  pipelineUnusableVolume!: number

  @Expose()
  pipelineSecondaryVolume!: number

  @Expose()
  pipelineSecondaryUnusableVolume!: number

  /**
   * Constructor for VolumeInventorySnapshotEntryEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(containerVolumeInventorySnapshotEntry: VolumeInventorySnapshotEntrySnapshotEntry): VolumeInventorySnapshotEntryEntity {
    return plainToInstance(
      VolumeInventorySnapshotEntryEntity,
      containerVolumeInventorySnapshotEntry,
      {
        excludeExtraneousValues: true,
      }
    );
  }

  loadTypeValue(): Promise<VolumeInventorySnapshotEntrySnapshotEntry> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(VolumeInventorySnapshotEntryValidator, object);
  }

  asTypeValue(): VolumeInventorySnapshotEntrySnapshotEntry {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(VolumeInventorySnapshotEntryValidator, object);
  }
}

export type VolumeInventoryVolumeType = DatabaseCode;

const usable: VolumeInventoryVolumeType = {
  code: 'USABLE',
  displayValue: {
    id: 'common.insight.volumeinventory.volumetype.usable',
    defaultMessage: 'Usable Volume',
  },
};

const unusable: VolumeInventoryVolumeType = {
  code: 'UNUSABLE',
  displayValue: {
    id: 'common.insight.volumeinventory.volumetype.unusable',
    defaultMessage: 'Unusable Volume',
  },
};

const secondaryUsable: VolumeInventoryVolumeType = {
  code: 'SECONDARY_USABLE',
  displayValue: {
    id: 'common.insight.volumeinventory.volumetype.secondaryusable',
    defaultMessage: 'Secondary Usable Volume',
  },
};

const secondaryUnusable: VolumeInventoryVolumeType = {
  code: 'SECONDARY_UNUSABLE',
  displayValue: {
    id: 'common.insight.volumeinventory.volumetype.secondaryunusable',
    defaultMessage: 'Secondary Unusable Volume',
  },
};

export const volumeInventoryVolumeType = {
  usable,
  unusable,
  secondaryUsable,
  secondaryUnusable,
};

export const volumeInventoryVolumeTypes = Object.values(volumeInventoryVolumeType);

export const toVolumeInventoryVolumeType = (code: string): VolumeInventoryVolumeType => toDatabaseCode(code, volumeInventoryVolumeTypes)
